import React, { useRef } from "react";
import {
  Check,
  CircleAlert,
  Cloud,
  Database,
  FileJson,
  GitCompareArrows,
  History,
  Save,
  ServerOff,
  ShieldCheck,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import {
  CardHeader,
  MetricCard,
  PageHeading,
  StatusChip,
  WelcomeEmpty,
} from "./AuditUI";

export function GovernancePage({
  container,
  analysis,
  governance,
  policy,
  setPolicy,
  resetPolicy,
  loadDemo,
}) {
  if (!container || !analysis || !governance) {
    return (
      <section className="page">
        <PageHeading
          eyebrow="Governance / Controls"
          title="Policy gates and remediation backlog"
          description="Apply measurable release controls to the deterministic audit."
        />
        <WelcomeEmpty onLoadDemo={loadDemo} />
      </section>
    );
  }

  return (
    <section className="page">
      <PageHeading
        eyebrow="Governance / Controls"
        title="Policy gates and remediation backlog"
        description="Turn audit findings into release criteria, ownership, effort estimates, and prioritized work."
        actions={
          <StatusChip tone={governance.failed ? "warning" : "success"} icon={ShieldCheck}>
            {governance.passed}/{governance.total} controls passed
          </StatusChip>
        }
      />

      <div className="metrics-grid governance-metrics">
        <MetricCard value={`${governance.score}`} label="Policy score" />
        <MetricCard value={`${governance.coverage.consent}%`} label="Consent coverage" />
        <MetricCard value={`${governance.coverage.triggerIntegrity}%`} label="Trigger integrity" />
        <MetricCard value={`${governance.graph.variableUtilization}%`} label="Variable utilization" />
        <MetricCard value={governance.riskRegister.length} label="Risk groups" tone="error" />
      </div>

      <div className="governance-grid">
        <article className="card policy-card">
          <CardHeader
            step="01"
            title="Release policy"
            description="Set the minimum quality bar for this workspace."
            action={
              <button type="button" className="text-button" onClick={resetPolicy}>
                Reset
              </button>
            }
          />
          <div className="policy-fields">
            <label>
              Policy profile
              <select
                value={policy.profile}
                onChange={(event) => setPolicy(policyForProfile(event.target.value))}
              >
                <option value="standard">Standard</option>
                <option value="strict">Strict production</option>
                <option value="exploratory">Exploratory</option>
              </select>
            </label>
            <NumberPolicy
              label="Minimum overall score"
              value={policy.minimumOverallScore}
              setValue={(value) => setPolicy({ ...policy, minimumOverallScore: value })}
            />
            <NumberPolicy
              label="Minimum naming score"
              value={policy.minimumNamingScore}
              setValue={(value) => setPolicy({ ...policy, minimumNamingScore: value })}
            />
            <NumberPolicy
              label="Minimum consent coverage"
              value={policy.minimumConsentCoverage}
              setValue={(value) => setPolicy({ ...policy, minimumConsentCoverage: value })}
              suffix="%"
            />
            <NumberPolicy
              label="Maximum paused tags"
              value={policy.maximumPausedTags}
              setValue={(value) => setPolicy({ ...policy, maximumPausedTags: value })}
            />
            <label className="check-label compact-check">
              <input
                type="checkbox"
                checked={policy.allowCustomHtml}
                onChange={(event) => setPolicy({ ...policy, allowCustomHtml: event.target.checked })}
              />
              Allow Custom HTML tags
            </label>
            <label className="check-label compact-check">
              <input
                type="checkbox"
                checked={policy.requireGa4EventTags}
                onChange={(event) =>
                  setPolicy({ ...policy, requireGa4EventTags: event.target.checked })
                }
              />
              Require at least one GA4 event tag
            </label>
          </div>
        </article>

        <article className="card controls-card">
          <CardHeader
            step="02"
            title="Control results"
            description="A failed control should block or require approval before publish."
          />
          <div className="control-list">
            {governance.controls.map((item) => (
              <div key={item.name} className={item.passed ? "pass" : "fail"}>
                {item.passed ? <Check aria-hidden="true" /> : <X aria-hidden="true" />}
                <div>
                  <strong>{item.name}</strong>
                  <small>Actual: {item.actual} · Target: {item.target}</small>
                </div>
                <StatusChip tone={item.passed ? "success" : "error"}>
                  {item.passed ? "Pass" : "Fail"}
                </StatusChip>
              </div>
            ))}
          </div>
        </article>
      </div>

      <article className="card dependency-card">
        <CardHeader
          step="03"
          title="Dependency health"
          description="A compact view of how tags, triggers, and variables connect."
        />
        <dl className="dependency-stats">
          <div><dt>Objects</dt><dd>{governance.graph.nodes}</dd></div>
          <div><dt>Tag → trigger links</dt><dd>{governance.graph.tagTriggerEdges}</dd></div>
          <div><dt>Trigger utilization</dt><dd>{governance.graph.triggerUtilization}%</dd></div>
          <div><dt>Variable utilization</dt><dd>{governance.graph.variableUtilization}%</dd></div>
          <div><dt>Custom HTML tags</dt><dd>{governance.customHtmlCount}</dd></div>
        </dl>
      </article>

      <article className="card risk-card">
        <CardHeader
          step="04"
          title="Prioritized remediation backlog"
          description="Related findings are grouped into assignable work items."
          action={<StatusChip>{governance.riskRegister.length} groups</StatusChip>}
        />
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Priority</th>
                <th>Risk</th>
                <th>Count</th>
                <th>Owner</th>
                <th>Effort</th>
                <th>Recommended action</th>
              </tr>
            </thead>
            <tbody>
              {governance.riskRegister.map((item) => (
                <tr key={`${item.section}-${item.category}`}>
                  <td><span className={`priority-chip ${item.priority.toLowerCase()}`}>{item.priority}</span></td>
                  <td className="item-cell">{item.category}</td>
                  <td>{item.count}</td>
                  <td>{item.owner}</td>
                  <td>{item.effort}</td>
                  <td className="description-cell">{item.recommendation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </article>
    </section>
  );
}

export function ComparePage({
  container,
  baseline,
  baselineName,
  baselineError,
  comparison,
  onBaselineFile,
  clearBaseline,
  loadDemo,
}) {
  const fileInput = useRef(null);
  if (!container) {
    return (
      <section className="page">
        <PageHeading
          eyebrow="Change control / Compare"
          title="Compare container versions"
          description="Load the current container first, then add a baseline export."
        />
        <WelcomeEmpty onLoadDemo={loadDemo} />
      </section>
    );
  }

  return (
    <section className="page">
      <PageHeading
        eyebrow="Change control / Compare"
        title="Compare container versions"
        description="Detect added, removed, renamed, retyped, and paused objects using stable GTM IDs."
        actions={
          baseline ? (
            <StatusChip tone="success" icon={GitCompareArrows}>
              Baseline loaded
            </StatusChip>
          ) : (
            <StatusChip>Awaiting baseline</StatusChip>
          )
        }
      />

      <article className="card compare-upload-card">
        <CardHeader
          step="01"
          title={baseline ? baselineName : "Import baseline container"}
          description="The baseline is parsed locally and never uploaded."
          action={
            baseline ? (
              <button type="button" className="text-button" onClick={clearBaseline}>
                <Trash2 aria-hidden="true" /> Remove baseline
              </button>
            ) : null
          }
        />
        <input
          ref={fileInput}
          type="file"
          accept=".json,application/json"
          hidden
          onChange={(event) => onBaselineFile(event.target.files?.[0])}
        />
        <button type="button" className="compare-drop" onClick={() => fileInput.current?.click()}>
          <UploadCloud aria-hidden="true" />
          <span>
            <strong>{baseline ? "Replace baseline JSON" : "Choose baseline GTM export"}</strong>
            <small>Maximum 15 MB · processed in this browser</small>
          </span>
        </button>
        {baselineError ? <p className="field-error">{baselineError}</p> : null}
      </article>

      {comparison ? (
        <>
          <div className="comparison-score-grid">
            {Object.entries(comparison.scoreDelta).map(([key, value]) => (
              <article className="metric-card" key={key}>
                <strong className={value > 0 ? "positive" : value < 0 ? "negative" : ""}>
                  {value === null ? "N/A" : `${value > 0 ? "+" : ""}${value}`}
                </strong>
                <span>{capitalize(key)} score delta</span>
              </article>
            ))}
            <MetricCard value={comparison.changeCount} label="Object changes" />
          </div>

          <div className="comparison-summary">
            {Object.entries(comparison.summary).map(([key, item]) => (
              <article className="card" key={key}>
                <span>{capitalize(key)}</span>
                <strong>{item.before} → {item.after}</strong>
                <small>+{item.added} added · {item.removed} removed · {item.modified} modified</small>
              </article>
            ))}
          </div>

          <article className="card">
            <CardHeader
              step="02"
              title="Change ledger"
              description="Review every object-level difference before a GTM publish."
              action={
                <StatusChip tone={comparison.regression ? "warning" : "success"}>
                  {comparison.regression ? `${comparison.regression}-point regression` : "No overall regression"}
                </StatusChip>
              }
            />
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Change</th><th>Object</th><th>Baseline name</th><th>Current name</th><th>Detail</th></tr>
                </thead>
                <tbody>
                  {comparison.changes.length ? comparison.changes.map((item, index) => (
                    <tr key={`${item.objectType}-${item.change}-${index}`}>
                      <td><span className={`change-chip ${item.change}`}>{item.change}</span></td>
                      <td>{item.objectType}</td>
                      <td className="item-cell">{item.baselineName}</td>
                      <td className="item-cell">{item.currentName}</td>
                      <td>{item.detail}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan="5"><div className="small-empty"><Check aria-hidden="true" /> No object changes detected.</div></td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </article>
        </>
      ) : null}
    </section>
  );
}

export function HistoryPage({
  container,
  analysis,
  backendStatus,
  backendFeatures,
  history,
  historyStatus,
  historyError,
  saveCurrent,
  refreshHistory,
  deleteSaved,
  loadDemo,
}) {
  const historyEnabled = backendStatus === "online" && backendFeatures?.auditHistory;
  return (
    <section className="page">
      <PageHeading
        eyebrow="Cloud workspace / History"
        title="Sanitized audit history"
        description="Save ratings and top findings in D1 without storing the raw GTM container."
        actions={
          <StatusChip
            tone={historyEnabled ? "success" : "warning"}
            icon={historyEnabled ? Database : ServerOff}
          >
            {historyEnabled ? "D1 connected" : "D1 not configured"}
          </StatusChip>
        }
      />

      {!container || !analysis ? <WelcomeEmpty onLoadDemo={loadDemo} /> : null}

      <article className="card history-actions-card">
        <CardHeader
          step="01"
          title="Workspace snapshots"
          description="Only source labels, ratings, counts, policy results, and up to 30 sanitized findings are stored."
        />
        <div className="button-row">
          <button
            type="button"
            className="primary-button"
            onClick={saveCurrent}
            disabled={!historyEnabled || !analysis || historyStatus === "saving"}
          >
            <Save aria-hidden="true" />
            {historyStatus === "saving" ? "Saving…" : "Save current audit"}
          </button>
          <button
            type="button"
            className="secondary-button"
            onClick={refreshHistory}
            disabled={!historyEnabled || historyStatus === "loading"}
          >
            <History aria-hidden="true" />
            Refresh history
          </button>
        </div>
        {!historyEnabled ? (
          <div className="backend-notice">
            <Cloud aria-hidden="true" />
            <div>
              <strong>AI proxy works without D1</strong>
              <p>Add a D1 binding named <code>DB</code> and run the included migration to enable history.</p>
            </div>
          </div>
        ) : null}
        {historyError ? <p className="field-error">{historyError}</p> : null}
      </article>

      <article className="card">
        <CardHeader
          step="02"
          title="Saved audits"
          description="Snapshots are private to the random workspace token held by this browser."
          action={<StatusChip>{history.length} saved</StatusChip>}
        />
        <div className="history-list">
          {history.length ? history.map((item) => (
            <div key={item.id} className="history-row">
              <span className="history-icon"><FileJson aria-hidden="true" /></span>
              <div>
                <strong>{item.containerName || item.sourceName}</strong>
                <small>{item.sourceName} · {formatDate(item.createdAt)}</small>
              </div>
              <dl>
                <div><dt>Overall</dt><dd>{item.scores?.overall ?? "—"}</dd></div>
                <div><dt>Naming</dt><dd>{item.scores?.naming ?? "—"}</dd></div>
                <div><dt>Findings</dt><dd>{item.findingCount ?? "—"}</dd></div>
              </dl>
              <button
                type="button"
                className="icon-button"
                aria-label={`Delete ${item.sourceName}`}
                onClick={() => deleteSaved(item.id)}
              >
                <Trash2 aria-hidden="true" />
              </button>
            </div>
          )) : (
            <div className="small-empty">
              <History aria-hidden="true" />
              {historyEnabled ? "No saved audits in this browser workspace." : "Connect D1 to save audit history."}
            </div>
          )}
        </div>
      </article>
    </section>
  );
}

function NumberPolicy({ label, value, setValue, suffix }) {
  return (
    <label>
      {label}
      <span className="number-field">
        <input
          type="number"
          min="0"
          max="100"
          value={value}
          onChange={(event) => setValue(Math.max(0, Math.min(100, Number(event.target.value) || 0)))}
        />
        {suffix ? <i>{suffix}</i> : null}
      </span>
    </label>
  );
}

function policyForProfile(profile) {
  if (profile === "strict") {
    return {
      profile,
      minimumOverallScore: 85,
      minimumNamingScore: 80,
      minimumConsentCoverage: 100,
      maximumPausedTags: 0,
      allowCustomHtml: false,
      requireGa4EventTags: true,
    };
  }
  if (profile === "exploratory") {
    return {
      profile,
      minimumOverallScore: 60,
      minimumNamingScore: 55,
      minimumConsentCoverage: 60,
      maximumPausedTags: 20,
      allowCustomHtml: true,
      requireGa4EventTags: false,
    };
  }
  return {
    profile: "standard",
    minimumOverallScore: 75,
    minimumNamingScore: 70,
    minimumConsentCoverage: 80,
    maximumPausedTags: 5,
    allowCustomHtml: true,
    requireGa4EventTags: true,
  };
}

function formatDate(value) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value || "Unknown date";
  }
}

function capitalize(value) {
  return value ? value[0].toUpperCase() + value.slice(1) : "";
}
